import type { LibraryItemView } from '@/lib/api/generated/model';
import { directLinks } from './installTree';

export interface RowSummaryLink {
  key: string;
  /** The library's name for it, or its key when the library does not hold it. */
  name: string;
  /** Whether this workspace already holds it, for the row's check mark. */
  present: boolean;
}

export interface RowSummary {
  links: RowSummaryLink[];
  agents: number;
  skills: number;
  /** The counts in words — "2 agents, 1 skill" — or empty when there are none. */
  needs: string;
  /** The whole line in words, for its `title` and for anyone reading it aloud. */
  text: string;
}

function count(n: number, one: string): string {
  return `${String(n)} ${n === 1 ? one : `${one}s`}`;
}

/**
 * What a workflow is made of, in one line for its row (spec 109 US3).
 *
 * The workflows it links to come first, by name, because for a composite
 * they are the headline; the agents and skills its install needs are then
 * counted by kind, however deep they sit. Three chips taken from the front of
 * the install list used to stand here, and the install list starts at the
 * innermost workflow — so a nested workflow's row led with its leaf's agents.
 */
export function rowSummary(
  item: LibraryItemView,
  items: readonly LibraryItemView[],
  present: ReadonlySet<string>,
): RowSummary {
  const requires = item.requires ?? [];
  const links = directLinks(item, items).map((key) => ({
    key,
    name:
      items.find((i) => i.kind === 'workflow' && i.key === key)?.name ?? key,
    present: present.has(`workflow/${key}`),
  }));
  const agents = requires.filter((dep) => dep.kind === 'agent').length;
  const skills = requires.filter((dep) => dep.kind === 'skill').length;

  const needs = [
    ...(agents > 0 ? [count(agents, 'agent')] : []),
    ...(skills > 0 ? [count(skills, 'skill')] : []),
  ].join(', ');
  const parts = [
    ...(links.length > 0
      ? [`Links ${links.map((l) => l.name).join(', ')}`]
      : []),
    ...(needs ? [links.length > 0 ? `needs ${needs}` : `Needs ${needs}`] : []),
  ];
  return { links, agents, skills, needs, text: parts.join(' · ') };
}
